import React, { memo, useState, useEffect } from "react";
import { Avatar, Space, Popover, Divider, Typography, Button, Tag, Modal, Form, Input } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LogoutOutlined,
  ProfileOutlined,
  IdcardOutlined,
  ShopOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { notify } from "../components/Customs/NotifyProvider";
import { isPlatformOwnerRole } from "../utils/permissionUtils";
import userService from "../services/userService";

const { Text } = Typography;

const HeaderBar = () => {
  const navigate = useNavigate();

  const [storedUser, setStoredUser] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [credentialsForm] = Form.useForm();

  useEffect(() => {
    if (popoverOpen) {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      setStoredUser(user);
    }
  }, [popoverOpen]);

  const handleReloadPermission = async () => {
    try {
      if (!storedUser?.user_id) {
        notify.error("User ID not found!");
        return;
      }

      let response;
      // const response = await PermissionService.getPermissionDetailsById(storedUser.user_id);

      localStorage.setItem(
        "permissions",
        JSON.stringify(response.data.permissions)
      );

      notify.success("Permissions reloaded successfully!");
    } catch (error) {
      notify.error("Failed to reload permissions.");
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    localStorage.removeItem("token");
    setPopoverOpen(false);
    navigate("/login", { replace: true });
  };

  const openCredentialsModal = () => {
    credentialsForm.setFieldsValue({
      username: storedUser?.username || "",
      password: "",
      confirmPassword: "",
    });
    setPopoverOpen(false);
    setCredentialsModalOpen(true);
  };

  // Platform Owner has no restaurant of their own, so they don't appear in
  // Manage Users (restaurant-scoped) or Manage Restaurants (which lists
  // OTHER restaurants they created) — this is the only place they can
  // change their own login. updateUser still requires full_name/role/status
  // in the payload even though only username/password are actually
  // changing here, so the current values are carried along unchanged.
  const handleSaveCredentials = async (values) => {
    if (values.password && values.password !== values.confirmPassword) {
      notify.error("Passwords do not match");
      return;
    }

    setSavingCredentials(true);
    try {
      const payload = {
        full_name: storedUser.full_name,
        username: values.username,
        email: storedUser.email,
        phone: storedUser.phone,
        role: storedUser.role,
        status: storedUser.status,
      };
      if (values.password) payload.password = values.password;

      const res = await userService.updateUser(storedUser.user_id, payload);

      if (res?.status) {
        const updatedUser = { ...storedUser, username: values.username };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setStoredUser(updatedUser);
        setCredentialsModalOpen(false);
        notify.success("Login details updated successfully");
      } else {
        notify.error(res?.message || "Failed to update login details");
      }
    } catch (error) {
      notify.error(error?.message || "Failed to update login details");
    } finally {
      setSavingCredentials(false);
    }
  };

  const popoverContent = (
    <div className="header-user-popover">
      <div className="header-user-top">
        <Avatar
          size={52}
          className="header-user-popover-avatar"
          icon={<UserOutlined />}
        />
        <div className="header-user-top-content">
          <div className="header-user-name">{storedUser?.full_name ?? "-"}</div>
          {storedUser?.restaurant_name && (
            <div className="header-user-restaurant">
              <ShopOutlined /> {storedUser.restaurant_name}
            </div>
          )}
          <div className="header-user-email">{storedUser?.email ?? "-"}</div>
          <div className="header-user-status">
            <Tag color={storedUser?.status === "active" ? "green" : "red"}>
              {storedUser?.status ?? "-"}
            </Tag>
          </div>
        </div>
      </div>

      <Divider className="header-user-divider" />

      <div className="header-user-grid">
        <Text strong>
          <IdcardOutlined /> User ID
        </Text>
        <Text>{storedUser?.user_id ?? "-"}</Text>

        <Text strong>
          <UserOutlined /> Username
        </Text>
        <Text>{storedUser?.username ?? "-"}</Text>

        <Text strong>
          <PhoneOutlined /> Phone
        </Text>
        <Text>{storedUser?.phone ?? "-"}</Text>

        <Text strong>
          <MailOutlined /> Role
        </Text>
        <Text>{storedUser?.role ?? "-"}</Text>
      </div>

      <Divider className="header-user-divider" />

      <div className="header-user-actions">
        <Button
          block
          className="secondary-btn"
          icon={<ProfileOutlined />}
          onClick={() => {
            setPopoverOpen(false);
            navigate("/profile");
          }}
        >
          View Profile
        </Button>

        {isPlatformOwnerRole(storedUser) && (
          <Button
            block
            className="secondary-btn"
            icon={<LockOutlined />}
            onClick={openCredentialsModal}
          >
            Change Username / Password
          </Button>
        )}

        <Button
          block
          danger
          icon={<LogoutOutlined />}
          onClick={handleSignOut}
        >
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Space size="large">
        <Popover
          content={popoverContent}
          trigger="click"
          placement="bottomRight"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          overlayClassName="header-user-popover-overlay"
        >
          <div className="header-user-trigger">
            <Avatar className="header-user-avatar" icon={<UserOutlined />} />
          </div>
        </Popover>
      </Space>

      <Modal
        title="Change Username / Password"
        open={credentialsModalOpen}
        onCancel={() => setCredentialsModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={credentialsForm}
          layout="vertical"
          onFinish={handleSaveCredentials}
        >
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: "Please enter username" }]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="password"
            extra="Leave blank to keep the current password"
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={["password"]}
          >
            <Input.Password placeholder="Re-enter new password" />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setCredentialsModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={savingCredentials}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default memo(HeaderBar);